import {
  federationConfigLoader,
  type FederationConfig,
  type RemoteConfig,
  type ComponentConfig,
} from '@/lib/moduleFederation/config/federationConfigLoader';

// Minimal type declarations for MF runtime
declare global {
  interface Window {
    __webpack_share_scopes__?: any;
  }
}

// Initialize shared scope for React Query
if (typeof window !== 'undefined' && !window.__webpack_share_scopes__) {
  window.__webpack_share_scopes__ = {
    default: {
      '@tanstack/react-query': {
        '4.29.5': {
          get: () => Promise.resolve(() => require('@tanstack/react-query')),
          loaded: 1,
          from: 'aga-frontend',
        },
      },
      react: {
        '18.2.0': {
          get: () => Promise.resolve(() => require('react')),
          loaded: 1,
          from: 'aga-frontend',
        },
      },
      'react-dom': {
        '18.2.0': {
          get: () => Promise.resolve(() => require('react-dom')),
          loaded: 1,
          from: 'aga-frontend',
        },
      },
    },
  };
}

// Vite remotes embed a preload-helper that creates <link rel="modulepreload">
// tags with paths like "/assets/Foo.js". These resolve against the document
// origin (localhost:3002) instead of the remote origin, causing 404s.
// The actual ES import() calls resolve correctly against the remote URL.
//
// Two layers of defense:
// 1. Intercept appendChild to prevent the broken <link> tags from being
//    added. Fire a synthetic "load" event so the Vite preload-helper's
//    Promise resolves and the real import() proceeds.
// 2. Handle vite:preloadError as a safety net for any preload failures
//    that slip through.
if (typeof window !== 'undefined') {
  const origAppendChild = HTMLHeadElement.prototype.appendChild;
  HTMLHeadElement.prototype.appendChild = function <T extends Node>(node: T): T {
    if (
      node instanceof HTMLLinkElement &&
      (node.rel === 'modulepreload' || node.rel === 'preload') &&
      node.href &&
      new URL(node.href, document.baseURI).origin === window.location.origin &&
      new URL(node.href, document.baseURI).pathname.startsWith('/assets/')
    ) {
      queueMicrotask(() => node.dispatchEvent(new Event('load')));
      return node;
    }
    return origAppendChild.call(this, node) as T;
  };

  window.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault();
  });
}

// Initialize the config loader
let configPromise: Promise<FederationConfig> | null = null;

// Helper to ensure config is loaded
const ensureConfig = async (): Promise<FederationConfig> => {
  if (!configPromise) {
    configPromise = federationConfigLoader.loadConfig();
  }
  return configPromise;
};

// Helper function to get federation config for a component
export const getFederationConfig = async (
  remote: string,
  component: string,
  version?: string,
  fallbackVersion?: string,
) => {
  const config = await ensureConfig();

  const remoteConfig = federationConfigLoader.getRemoteConfig(remote);
  const componentConfig = federationConfigLoader.getComponentConfig(remote, component);

  if (!remoteConfig) {
    throw new Error(`Remote "${remote}" not found in federation config`);
  }

  if (!componentConfig) {
    throw new Error(`Component "${component}" not found in remote "${remote}"`);
  }

  // Use version-aware url resolution
  const { resolveRemoteUrl } = await import('@/lib/moduleFederation/config/federationConfigLoader');
  const { url: remoteUrl, version: resolvedVersion } = resolveRemoteUrl(remoteConfig, version, fallbackVersion);

  return {
    remoteUrl,
    scope: (remoteConfig as any).scope,
    module: componentConfig.module,
    version: resolvedVersion,
    availableVersions: (remoteConfig as any).versions ? Object.keys((remoteConfig as any).versions) : undefined,
    defaultVersion: (remoteConfig as any).version,
  };
};

// Helper to get all available remotes and their connectivity
export const getAvailableRemotes = async (): Promise<{
  remotes: Record<string, RemoteConfig>;
  connectivity: Record<string, boolean>;
}> => {
  await ensureConfig();
  const remotes = federationConfigLoader.getAllRemotes();
  const connectivity: Record<string, boolean> = {};
  await Promise.all(
    Object.entries(remotes).map(async ([remoteName, remoteConfig]) => {
      try {
        const { resolveRemoteUrl } = await import('./federationConfigLoader');
        const { url } = resolveRemoteUrl(remoteConfig);
        if (!url) throw new Error('No URL');
        const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        connectivity[remoteName] = res.ok;
      } catch {
        connectivity[remoteName] = false;
      }
    }),
  );
  return { remotes, connectivity };
};

// Helper to get all available components
export const getAvailableComponents = async (): Promise<Record<string, Record<string, ComponentConfig>>> => {
  await ensureConfig();
  // getAllComponents is now async
  return await federationConfigLoader.getAllComponents();
};

// Helper to get component preset
export const getComponentPreset = async (presetName: string): Promise<string[]> => {
  await ensureConfig();
  return federationConfigLoader.getPreset(presetName);
};

// Helper to get federation features
export const getFederationFeatures = async () => {
  await ensureConfig();
  return federationConfigLoader.getFeatures();
};

// Helper to check if a remote/component exists
export const isComponentAvailable = async (remote: string, component: string): Promise<boolean> => {
  try {
    await getFederationConfig(remote, component);
    return true;
  } catch {
    return false;
  }
};

// Helper to get all components from a specific remote
export const getRemoteComponents = async (remote: string): Promise<Record<string, ComponentConfig>> => {
  const allComponents = await getAvailableComponents();
  return allComponents[remote] || {};
};

// Preload config (call this early in your app). Returns a promise so callers
// can `await` to guarantee the config is loaded before doing anything else.
export const preloadFederationConfig = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  await ensureConfig();
};

// Hook into config updates
export const onConfigUpdate = (callback: (config: FederationConfig) => void) => {
  if (typeof window !== 'undefined') {
    window.addEventListener('federation-config-updated', event => {
      callback((event as CustomEvent).detail);
    });
  }
};

// Export the config loader for advanced usage
export { federationConfigLoader, RemoteConfig, ComponentConfig };

// Cache for preloaded and initialized remotes
const preloadedRemotes = new Map<string, any>();
// Deduplicates concurrent prefetch calls (e.g. from Strict Mode double-invoke)
const pendingPrefetches = new Map<string, Promise<void>>();

// Enhanced prefetch that also initializes the remote.
// Always awaits the federation config so we resolve the full remote URL from
// the loaded config rather than relying on a possibly-incomplete object.
export async function prefetchRemoteEntry(remoteConfig: RemoteConfig) {
  if (typeof window === 'undefined') return;

  await ensureConfig();

  const resolved = federationConfigLoader.getRemoteConfig(remoteConfig.scope) || remoteConfig;
  const { resolveRemoteUrl } = await import('@/lib/moduleFederation/config/federationConfigLoader');
  const { url } = resolveRemoteUrl(resolved);
  if (!url) return;

  if (preloadedRemotes.has(resolved.scope)) return;

  // If a prefetch for this scope is already in flight, coalesce onto it
  if (pendingPrefetches.has(resolved.scope)) {
    return pendingPrefetches.get(resolved.scope);
  }

  const work = (async () => {
    console.log(`[Prefetch] Loading and initializing ${resolved.scope} from ${url}`);

    try {
      const { loadRemoteEntry } = await import('@/lib/moduleFederation/hooks/useFederatedComponent');
      const remote = await loadRemoteEntry(url);

      if (remote.init && typeof remote.init === 'function') {
        const { createHostSharedShareMap } = await import('@/lib/moduleFederation/runtime/hostShared');
        const hostShared = createHostSharedShareMap('aga-frontend');
        await remote.init(hostShared);
      }

      if (remote.get && typeof remote.get === 'function') {
        try {
          const bootstrapFactory = await remote.get('./agGridBootstrap');
          if (typeof bootstrapFactory === 'function') bootstrapFactory();
        } catch {
          // Bootstrap module may not exist in all remotes
        }
      }

      preloadedRemotes.set(resolved.scope, remote);

      if (typeof window !== 'undefined') {
        window.__PRELOADED_REMOTES__ = window.__PRELOADED_REMOTES__ || {};
        window.__PRELOADED_REMOTES__[resolved.scope] = remote;
      }
    } catch (error) {
      console.error(`[Prefetch] Failed to preload ${resolved.scope}:`, error);
    } finally {
      pendingPrefetches.delete(resolved.scope);
    }
  })();

  pendingPrefetches.set(resolved.scope, work);
  return work;
}

// Export helper to get preloaded remotes
export function getPreloadedRemote(scope: string): any | undefined {
  return (
    preloadedRemotes.get(scope) || (typeof window !== 'undefined' ? window.__PRELOADED_REMOTES__?.[scope] : undefined)
  );
}

// Types for backward compatibility
export type AvailableRemotes = string;
export type AvailableComponents<T extends string> = string;
